import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as LightweightCharts from 'lightweight-charts';
import axios from 'axios';
import './HlTradingChart.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const INTERVALS = [
  { id: '1m', label: '1m' },
  { id: '5m', label: '5m' },
  { id: '15m', label: '15m' },
  { id: '1h', label: '1h' },
  { id: '4h', label: '4h' },
  { id: '1d', label: '1D' },
];

const ZOOM_IN_FACTOR = 0.75;
const ZOOM_OUT_FACTOR = 1 / ZOOM_IN_FACTOR;
const MIN_VISIBLE_BARS = 8;
const MAX_VISIBLE_BARS = 800;

function candleLookbackHours(intervalId) {
  if (intervalId === '1d') return 720;
  if (intervalId === '4h') return 240;
  if (intervalId === '1m') return 24;
  return 72;
}

export default function HlTradingChart({
  symbol,
  hlDex = 'xyz',
  hlCoin = null,
  height = 420,
  apiPrefix = 'single-asset-hl',
  showZoomButtons = false,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const fittedRef = useRef(false);
  const [interval, setIntervalId] = useState('1m');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const zoomTimeScale = useCallback((factor) => {
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    const range = ts.getVisibleLogicalRange();
    if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) return;
    const span = Math.max(range.to - range.from, 1);
    const nextSpan = Math.min(MAX_VISIBLE_BARS, Math.max(MIN_VISIBLE_BARS, span * factor));
    const center = (range.from + range.to) / 2;
    ts.setVisibleLogicalRange({
      from: center - nextSpan / 2,
      to: center + nextSpan / 2,
    });
  }, []);

  const zoomIn = useCallback(() => zoomTimeScale(ZOOM_IN_FACTOR), [zoomTimeScale]);
  const zoomOut = useCallback(() => zoomTimeScale(ZOOM_OUT_FACTOR), [zoomTimeScale]);
  const resetZoom = useCallback(() => {
    chartRef.current?.timeScale()?.fitContent();
  }, []);

  const fetchCandles = useCallback(async () => {
    if (!symbol) return [];
    const hours = candleLookbackHours(interval);
    const { data } = await axios.get(`${API_BASE}/${apiPrefix}/candles/${symbol}`, {
      headers: authHeaders(),
      params: {
        dex: hlDex && hlDex !== 'main' ? hlDex : undefined,
        coin: hlCoin || undefined,
        interval,
        hours,
      },
    });
    return (data.candles || [])
      .filter((c) => Number.isFinite(c.time) && Number.isFinite(c.open))
      .map((c) => ({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }));
  }, [symbol, hlDex, hlCoin, interval, apiPrefix]);

  useEffect(() => {
    if (!containerRef.current || !symbol) {
      chartRef.current = null;
      seriesRef.current = null;
      return undefined;
    }

    let cancelled = false;
    const container = containerRef.current;
    const chart = LightweightCharts.createChart(container, {
      width: container.clientWidth || 600,
      height,
      layout: { background: { color: '#0b1020' }, textColor: '#cbd5e1' },
      grid: { vertLines: { color: '#1e293b' }, horzLines: { color: '#1e293b' } },
      rightPriceScale: { borderColor: '#334155' },
      timeScale: { borderColor: '#334155', timeVisible: true },
    });
    const series = chart.addCandlestickSeries({
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });
    chartRef.current = chart;
    seriesRef.current = series;
    fittedRef.current = false;

    const applyData = async ({ fit = false } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const candles = await fetchCandles();
        if (cancelled || !seriesRef.current) return;
        seriesRef.current.setData(candles);
        if (fit || !fittedRef.current) {
          chartRef.current?.timeScale()?.fitContent();
          fittedRef.current = true;
        }
        if (!candles.length) setError('No candle data for this market.');
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    applyData({ fit: true });
    const poll = setInterval(() => applyData({ fit: false }), 90000);

    const onResize = () => {
      if (container) chart.applyOptions({ width: container.clientWidth || 600 });
    };
    window.addEventListener('resize', onResize);
    requestAnimationFrame(onResize);

    return () => {
      cancelled = true;
      clearInterval(poll);
      window.removeEventListener('resize', onResize);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [symbol, hlDex, hlCoin, height, fetchCandles]);

  if (!symbol) {
    return <div className="hl-trading-chart empty">Select a market to load chart</div>;
  }

  return (
    <div className="hl-trading-chart">
      <div className="hl-chart-toolbar">
        <span className="hl-chart-symbol">{symbol}</span>
        <div className="hl-chart-intervals">
          {INTERVALS.map((iv) => (
            <button
              key={iv.id}
              type="button"
              className={interval === iv.id ? 'active' : ''}
              onClick={() => {
                fittedRef.current = false;
                setIntervalId(iv.id);
              }}
            >
              {iv.label}
            </button>
          ))}
        </div>
        {showZoomButtons && (
          <div className="hl-chart-zoom" role="group" aria-label="Chart zoom">
            <button type="button" className="hl-chart-zoom-btn" title="Zoom in" aria-label="Zoom in" onClick={zoomIn}>
              +
            </button>
            <button type="button" className="hl-chart-zoom-btn" title="Zoom out" aria-label="Zoom out" onClick={zoomOut}>
              −
            </button>
            <button type="button" className="hl-chart-zoom-btn hl-chart-zoom-reset" title="Reset zoom" aria-label="Reset zoom" onClick={resetZoom}>
              ↺
            </button>
          </div>
        )}
        {loading && <span className="hl-chart-loading">Updating…</span>}
      </div>
      {error && <p className="hl-chart-error">{error}</p>}
      <div ref={containerRef} className="hl-chart-canvas" style={{ height, minHeight: height }} />
    </div>
  );
}
